import { useMemo } from 'react';
import { RefreshCw, Star, ExternalLink, MessageSquare, BarChart3, Sparkles } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { MetricCard } from '@/shared/ui/MetricCard';
import { useToast } from '@/shared/ui/Toast';
import { BarChartCard } from '@/shared/ui/BarChartCard';
import type { BarChartTone } from '@/shared/ui/BarChartCard';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useLocation } from '@/shared/hooks/useLocation';
import { reviewsApi } from '../api/reviewsApi';
import type { Review } from '../api/reviewsApi';
import { reviewKeys } from '../queryKeys';
import { useReputationOverview } from '../hooks/useReputationOverview';
import { useReviews } from '../hooks/useReviews';
import { ReviewCard } from './ReviewCard';
import styles from './CockpitTab.module.css';

const THEME_TONES: BarChartTone[] = ['success', 'info', 'purple', 'warning', 'error', 'accent'];

interface CockpitTabProps {
  gbpConnected: boolean;
  onRespond: (review: Review) => void;
}

export function CockpitTab({ gbpConnected, onRespond }: CockpitTabProps) {
  const navigate = useNavigate();
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useReputationOverview();
  const {
    reviews,
    isLoading: reviewsLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useReviews('all');

  const themeTotal = useMemo(() => data?.themes.reduce((sum, t) => sum + t.count, 0) ?? 1, [data]);
  const themeRows = useMemo(
    () =>
      data?.themes.map((t, i) => ({
        label: t.label,
        value: t.count,
        display: `${Math.round((t.count / themeTotal) * 100)}%`,
        tone: THEME_TONES[i % THEME_TONES.length],
      })) ?? [],
    [data, themeTotal],
  );

  const recluster = useMutation({
    mutationFn: () => reviewsApi.reclusterThemes(locationId),
    onSuccess: (updated) => {
      queryClient.setQueryData(reviewKeys.reputation(locationId), updated);
      toast.success('Theme analysis updated.');
    },
    onError: (err: Error) => {
      toast.error(err.message ?? 'Failed to refresh themes. Try again.');
    },
  });

  if (!gbpConnected) {
    return (
      <EmptyState
        icon={<Star size={28} aria-hidden />}
        title="Connect Google Business Profile"
        description="Connect your GBP account to see your reputation overview, star distribution, and review themes."
        actions={
          <Button variant="primary" onClick={() => navigate('/settings/organic')}>
            <ExternalLink size={14} aria-hidden /> Connect GBP
          </Button>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (!data) return null;

  const replyRatePct = Math.round(data.replyRate * 100);
  const replyRateTone = replyRatePct >= 80 ? 'green' : replyRatePct >= 50 ? 'yellow' : 'red';
  const ratingTone = data.rating >= 4.5 ? 'green' : data.rating < 4 ? 'red' : 'yellow';

  return (
    <div className={styles.root}>
      {/* ── 4-card stat row ── */}
      <div className={patterns.statGrid4}>
        <MetricCard
          title="Google rating"
          value={data.rating.toFixed(1)}
          valueSuffix="★"
          sub={`${data.total} reviews · via GBP`}
          tone={ratingTone}
        />
        <MetricCard
          title="New (30 days)"
          value={data.new30d}
          change={data.new30d - data.prev30d}
          changeLabel="vs prior 30d"
        />
        <MetricCard
          title="Local benchmark"
          value={
            data.benchmark
              ? `${data.benchmark.avgRating.toFixed(1)} / ${data.benchmark.avgReviews.toLocaleString()}`
              : '—'
          }
          sub={data.benchmark ? 'avg rating / avg reviews nearby' : 'no competitor data yet'}
        />
        <MetricCard
          title="Reply rate"
          value={replyRatePct}
          valueSuffix="%"
          sub={`${data.notResponded} awaiting reply`}
          tone={replyRateTone}
        />
      </div>

      {/* ── Distribution + Themes side by side ── */}
      <div className={styles.twoCol}>
        <BarChartCard
          title="Rating distribution"
          icon={<BarChart3 size={13} aria-hidden />}
          max={100}
          rows={['5', '4', '3', '2', '1'].map((star) => {
            const pct = data.distribution[star] ?? 0;
            return { label: `${star} ★`, value: pct, display: `${pct}%` };
          })}
        />
        <BarChartCard
          title="What patients talk about"
          icon={<Sparkles size={13} aria-hidden />}
          subtitle={`AI-clustered from the text of ${data.total} reviews — confirms your caregiver positioning and flags the one soft spot.`}
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => recluster.mutate()}
              isLoading={recluster.isPending}
            >
              <RefreshCw size={12} aria-hidden /> Refresh
            </Button>
          }
          rows={themeRows}
          emptyState={
            <>
              <MessageSquare size={16} aria-hidden /> Theme analysis runs after 10+ reviews with
              text. Click Refresh to compute now.
            </>
          }
        />
      </div>

      {/* ── All reviews ── */}
      <div>
        <div className={patterns.sectionTitle}>Recent reviews</div>
        {reviewsLoading ? (
          <div className={styles.list}>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : reviews.length === 0 ? (
          <EmptyState
            icon={<MessageSquare size={24} aria-hidden />}
            title="No reviews yet"
            description="Once your Google Business Profile has reviews, they'll show up here."
          />
        ) : (
          <>
            <div className={styles.list}>
              {reviews.map((review) => (
                <ReviewCard key={review.id} review={review} onRespond={onRespond} />
              ))}
            </div>
            {hasNextPage && (
              <div className={styles.loadMore}>
                <Button
                  variant="secondary"
                  onClick={() => fetchNextPage()}
                  isLoading={isFetchingNextPage}
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
